import CloseCircleXIcon from '@assets/icons/close-circle-x-large.svg';
import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef } from 'react';
import { Checkbox } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import {
  FlatList,
  Platform,
  Pressable,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { SearchInput } from '@usepepper/ui/atoms/search-input';
import { shadow } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useEscapeKeyClose } from '@platform/shortcuts/hooks/use-escape-key-close';

const DEFAULT_MAX_VISIBLE_ITEMS = 6;
const DEFAULT_ITEM_HEIGHT = 56;

/** Stacking order within the component, low → high. The input wrapper sits
 *  on top so tapping the input doesn't trigger the native dismissal backdrop. */
const Z_INDEX = {
  BACKDROP: 15,
  CONTAINER: 10,
  DROPDOWN: 20,
  INPUT_WRAPPER: 25,
};

/**
 * Combobox-style component — a search input with an associated dropdown
 * listbox of checkbox items. Loosely follows the combobox pattern; full
 * keyboard navigation through results (arrow up/down, Enter to toggle) is
 * not implemented yet — see follow-up ticket.
 *
 * Dismissal:
 * - Web: clicking outside the component (document-level mousedown listener)
 * - Native/iPad: tapping the transparent backdrop rendered behind the dropdown
 * - All platforms: Escape key, or the X button in the search input
 *
 * @example
 * <SearchableCheckboxList<MyItemType>
 *   isOpen={isOpen}
 *   items={filteredItems}
 *   onClose={handleClose}
 *   onSearchChange={handleSearchChange}
 *   onToggleItem={handleToggle}
 *   renderItem={(item) => <Text>{item.name}</Text>}
 *   searchPlaceholder="Search items..."
 *   searchText={searchText}
 *   selectedKeys={selectedIds}
 * />
 */

export type CheckboxListItemData<T> = T & { key: string };

export interface SearchableCheckboxListProps<T> {
  containerStyle?: StyleProp<ViewStyle>;
  estimatedItemHeight?: number;
  footerText?: string;
  /** Optional screen-reader label for each row. The row already announces its
   *  checkbox role and checked state; this provides the item's identity. */
  getAccessibilityLabel?: (item: CheckboxListItemData<T>) => string;
  isOpen: boolean;
  items: CheckboxListItemData<T>[];
  listEmptyComponent?: React.ReactElement;
  /** Maximum number of rows visible before the dropdown scrolls. Defaults to 6. */
  maxVisibleItems?: number;
  onClose: () => void;
  onSearchChange: (text: string) => void;
  /** Fires when the search input gains focus. Lets the parent reopen the
   *  dropdown when the user refocuses an input that already has text. */
  onSearchInputFocus?: () => void;
  onToggleItem: (item: CheckboxListItemData<T>) => void;
  renderItem: (item: CheckboxListItemData<T>) => React.ReactElement;
  searchPlaceholder: string;
  searchText: string;
  selectedKeys: Set<string>;
}

export const SearchableCheckboxList = <T,>({
  containerStyle,
  estimatedItemHeight = DEFAULT_ITEM_HEIGHT,
  footerText,
  getAccessibilityLabel,
  isOpen,
  items,
  listEmptyComponent,
  maxVisibleItems = DEFAULT_MAX_VISIBLE_ITEMS,
  onClose,
  onSearchChange,
  onSearchInputFocus,
  onToggleItem,
  renderItem: renderItemContent,
  searchPlaceholder,
  searchText,
  selectedKeys,
}: SearchableCheckboxListProps<T>): React.ReactElement => {
  const [styles, theme] = useStyles();
  const containerRef = useRef<View>(null);
  const listRef = useRef<FlatList<CheckboxListItemData<T>>>(null);

  // Close on Escape key.
  useEscapeKeyClose(isOpen ? onClose : undefined);

  // Close on outside click (web only). Native uses the `<Pressable>` backdrop
  // rendered below; the document-level listener has no native equivalent.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!isOpen || Platform.OS !== 'web') {
      return;
    }
    const handleMouseDown = (e: MouseEvent) => {
      const node = containerRef.current as unknown as HTMLElement | null;
      if (node && !node.contains(e.target as Node)) {
        onCloseRef.current();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Clearing the search text is enough to dismiss the dropdown: the
  // parent's `onSearchChange('')` handler flips `isOpen` to false when
  // text is empty (whitespace included). See `quote-item-search-view.tsx`.
  const handleClear = useCallback(() => {
    onSearchChange('');
  }, [onSearchChange]);

  // Reset scroll when search changes so users always see the top of new results.
  useEffect(() => {
    listRef.current?.scrollToOffset({ animated: false, offset: 0 });
  }, [searchText]);

  const renderItem = useCallback(
    ({ item }: { item: CheckboxListItemData<T> }) => {
      const isSelected = selectedKeys.has(item.key);
      return (
        <Pressable
          accessibilityLabel={getAccessibilityLabel?.(item)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: isSelected }}
          onPress={() => onToggleItem(item)}
          style={({ hovered }) => [
            styles.row,
            isSelected && styles.rowSelected,
            hovered && styles.rowHovered,
          ]}
        >
          {/* The wrapping Pressable carries the checkbox role + state for
              VoiceOver / TalkBack. The visual Checkbox here is decorative;
              hide it so screen readers don't double-announce. */}
          <View
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            <Checkbox checked={isSelected} pointerEvents="none" size="$sm" />
          </View>
          <View style={styles.rowContent}>{renderItemContent(item)}</View>
        </Pressable>
      );
    },
    [
      getAccessibilityLabel,
      onToggleItem,
      renderItemContent,
      selectedKeys,
      styles,
    ],
  );

  const maxHeight = estimatedItemHeight * maxVisibleItems;

  return (
    <View ref={containerRef} style={styles.container}>
      <View style={styles.inputWrapper}>
        <SearchInput
          onFocus={onSearchInputFocus}
          onSearch={onSearchChange}
          placeholderText={searchPlaceholder}
          rightElement={
            searchText.length > 0 ? (
              <TouchableOpacity
                accessibilityLabel={i18n.t('clear_search')}
                accessibilityRole="button"
                hitSlop={20}
                onPress={handleClear}
                style={styles.closeButton}
              >
                <CloseCircleXIcon height={24} width={24} />
              </TouchableOpacity>
            ) : null
          }
          searchText={searchText}
        />
      </View>
      {/* Native outside-tap dismissal. Web uses a document-level mousedown
          listener instead (see useEffect above). The backdrop sits below the
          input wrapper and the dropdown via zIndex so neither is intercepted. */}
      {isOpen && Platform.OS !== 'web' ? (
        <Pressable
          // Hide from VoiceOver / TalkBack — dismiss is also reachable via
          // Escape and the X button, so the bare backdrop shouldn't be
          // landed on as a focusable element.
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          onPress={onClose}
          style={styles.backdrop}
        />
      ) : null}
      {isOpen && (searchText.length > 0 || items.length > 0) ? (
        <View style={[styles.listContainer, { maxHeight }, containerStyle]}>
          <FlatList<CheckboxListItemData<T>>
            data={items}
            keyExtractor={(item) => item.key}
            ListEmptyComponent={listEmptyComponent}
            maxToRenderPerBatch={15}
            ref={listRef}
            renderItem={renderItem}
          />
          {items.length > 0 && footerText ? (
            <View style={styles.footer}>
              <Typography
                color={theme.sk.color.text.tertiary}
                variant="paragraph-sm"
              >
                {footerText}
              </Typography>
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  // Spans the viewport via large negative offsets — RN won't clip absolute
  // children unless the parent sets `overflow: 'hidden'`.
  backdrop: {
    bottom: -2000,
    left: -2000,
    position: 'absolute',
    right: -2000,
    top: -2000,
    zIndex: Z_INDEX.BACKDROP,
  },
  closeButton: {
    borderRadius: 32,
  },
  container: {
    position: 'relative',
    zIndex: Z_INDEX.CONTAINER,
  },
  footer: {
    alignItems: 'center',
    borderTopColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  inputWrapper: {
    zIndex: Z_INDEX.INPUT_WRAPPER,
  },
  listContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    left: 0,
    marginTop: theme.sk.spacing.spaceXsmall,
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    top: '100%',
    zIndex: Z_INDEX.DROPDOWN,
    ...shadow({
      opacity: 0.1,
      radius: 16,
      x: 0,
      y: 4,
    }),
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
  },
  rowHovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  rowSelected: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
}));
